import { castArray, get, identity } from 'lodash-es'
import { UseFormGetFieldState } from 'react-hook-form'

import { GeoFence, GeofenceAlert } from '@/feature/observatory/types/GeoFence.types'
import { AssetTag } from '@/feature/tags'
import { getPointCoordinates } from '@/utils/geoFenceConverters'

// Imported by relative path rather than the `@/feature/observatory` barrel: the barrel
// re-exports this directory, so going through it would create an import cycle.
import { GeoMode, GeoFenceCreateSchemaType } from '../GeoFenceCreateSchema'

type FieldState = ReturnType<UseFormGetFieldState<Record<string, unknown>>>

export const shouldShowError = (fieldState: FieldState): boolean => {
  return fieldState.invalid && fieldState.isTouched
}

export const getFieldError = (fieldState: FieldState): string | undefined => {
  const error = fieldState.error
  if (!error) return undefined

  return get(castArray(error).find(identity), ['message'], undefined)
}

export const serializeContacts = (alert: GeofenceAlert | undefined) => {
  let contacts: string[] = []
  if (alert?.emails) {
    contacts = [...contacts, ...alert.emails]
  }
  if (alert?.phoneNumbers) {
    contacts = [...contacts, ...alert.phoneNumbers]
  }
  return contacts
}

/**
 * Resolves the representative map position for a geofence.
 *
 * Prefers the stored `home` coordinate, falls back to the geometry's point coordinates, and
 * finally — for LineStrings, which carry neither — uses the first coordinate of the line.
 */
export const getGeoFencePosition = (fence: GeoFence) => {
  let position = get(fence, 'geoJson.properties.home', getPointCoordinates(fence.geoJson))

  // For LineString geofences, use the first coordinate as representative position
  if (!position && get(fence, 'geoJson.geometry.type') === 'LineString') {
    const coordinates = get(fence, 'geoJson.geometry.coordinates', []) as number[][]
    if (coordinates.length > 0) {
      const [lng, lat] = coordinates[0]
      position = {
        lat: lat,
        lng: lng,
      }
    }
  }

  return position
}

export const formDefaults = (geoFence?: GeoFence): GeoFenceCreateSchemaType => {
  const geometryType = get(geoFence, 'geoJson.geometry.type')
  const geoFenceType =
    geometryType === 'Polygon'
      ? GeoMode.polygon
      : geometryType === 'LineString'
        ? GeoMode.linestring
        : GeoMode.circle

  // For LineString geofences, ensure we have a valid address
  const address =
    geoFence?.address ||
    (geoFenceType === GeoMode.linestring && get(geoFence, 'geoJson.properties.home')
      ? `${get(geoFence, 'geoJson.properties.home.lat')}, ${get(geoFence, 'geoJson.properties.home.lng')}`
      : '')

  const baseGeoFence = {
    title: geoFence?.title ?? '',
    highRisk: geoFence?.highRiskArea ?? false,
    radius: get(geoFence, 'geoJson.properties.radius', 250),
    address: address,
    type: geoFenceType,
    ...(geoFenceType === GeoMode.linestring
      ? { color: get(geoFence, 'geoJson.properties.color', undefined) as string | undefined }
      : {}),
  }

  // Only add points field for polygon geofences
  const geoFenceWithPoints =
    geoFenceType === GeoMode.polygon
      ? {
          ...baseGeoFence,
          points: get(geoFence, 'geoJson.geometry.coordinates[0]', []).map((p: number[]) => ({
            lat: p[1],
            lng: p[0],
          })),
        }
      : (baseGeoFence as GeoFenceCreateSchemaType['geoFence'])

  return {
    geoFence: geoFenceWithPoints,
    alerts:
      geoFence?.alertRules.map((alert) => ({
        id: alert.id,
        title: alert.title,
        enabledAlert: alert.enabledAlert,
        alertOnEnter: alert.alertOnEnter,
        alertOnExit: alert.alertOnExit,
        contacts: serializeContacts(alert),
        tags: alert.tags as AssetTag[],
        enableDwellTime: Number.isFinite(alert.dwellTime),
        dwellTime: alert.dwellTime,
      })) ?? [],
  }
}
